import {
  channelInfo,
  dealwithChannelChartKey,
} from '@op/components/ChannelLabel';
import {
  formatChartParamsDate,
  listTimeTitleFunc,
} from '@op/components/ChartCommon/formatter';
import EChartCommon, { getAxisPointer } from '@op/components/EChartCommon';
import useLanguage from '@op/hooks/useLanguage';
import { getPercentageInfo } from '@op/utils';
import {
  createChannelRichElement,
  formatTimeXAxisText,
  getChartPosition,
  getXAxisAxisLabel,
} from '@op/utils/chart';
import { dealWithOthersName } from '@op/utils/languageValue';
import { reviewsFormat, yAxisFormat } from '@op/utils/value';
import React, { useMemo } from 'react';
import { chartTitle } from '../../utils';

export default function BarAndLineChart(props: any) {
  const {
    chartType,
    type,
    chartData,
    filter,
    tooltipConfig,
    title,
    titleTips,
    rightTitle,
    rightTitleTips,
  } = props;

  const { formatMessage } = useLanguage();
  const eChartsRef = React.useRef(null as any);
  const options = useMemo(() => {
    const { newArr, timeArr: xAxisData, legendData } = chartData;
    const rich = type === 'channel' ? createChannelRichElement(newArr, true) : {};
    return {
      grid: {
        top: 20,
        contentLabel: true,
        left: 0,
        right: 5,
      },
      tooltip: {
        trigger: 'axis',
        appendToBody: false,
        confine: false,
        position: (pos: any, params: any, dom: any, rect: any, size: any) =>
          getChartPosition(pos, params, dom, rect, size, -5),
        formatter: (params: any) => {
          let currentDateTotal = 0;
          params?.forEach((item) => (currentDateTotal += item?.value));
          return formatChartParamsDate({
            params,
            total: currentDateTotal,
            isSort: true,
            valueFormat: (value: number) => reviewsFormat(value, 2),
            textShowFun: (marker: string, seriesName: string) => {
              const channelIcon =
                type === 'channel'
                  ? `<img src='${
                      channelInfo(seriesName.toLocaleLowerCase() as any).img
                    }'/>`
                  : '';
              return `<span class='echart-tooltip'><span class="chart-common-formatter-marker">${marker}</span>&nbsp;${channelIcon}${dealWithOthersName(
                seriesName,
                type,
              )}</span>`;
            },
            dealwithRecords(records, total) {
              const newRecords: Array<any> = records.map((item) => {
                const share = getPercentageInfo(item.originValue, total, 2);
                return {
                  ...item,
                  value: `${item.value}${
                    !item.originValue ? '' : ` (${share})`
                  }`,
                };
              });
              if (tooltipConfig?.showTotal) {
                newRecords.unshift({
                  text: formatMessage({
                    id: tooltipConfig?.title || 'Mentions',
                  }),
                  value: reviewsFormat(total),
                  originValue: total,
                  originText: '',
                });
              }
              return newRecords.filter((item) => Boolean(item));
            },
            tablesConfigFunc: (records: any) => {
              const columns = [
                {
                  key: 'text',
                  header: listTimeTitleFunc(params[0]?.name, filter.date_type),
                },
                {
                  key: 'value',
                  align: 'right',
                  weight: 'bold',
                  header: '',
                },
              ];

              return [
                {
                  showHeader: true,
                  records,
                  columns,
                },
              ];
            },
          });
        },
      },
      legend: {
        data: legendData,
        formatter: (value: any) =>
          `{${dealwithChannelChartKey(value)}|} {name|${dealWithOthersName(
            value,
            type,
          )}}`,
        rich,
      },
      axisPointer: getAxisPointer((data) =>
        formatTimeXAxisText(data.value, filter.date_type),
      ),
      xAxis: {
        type: 'category',
        axisLabel: getXAxisAxisLabel(filter),
        data: xAxisData,
        boundaryGap: ['20%', 0],
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: (value: any) => yAxisFormat(value),
        },
      },
      series: newArr?.map((val: any, index: number) => ({
        ...val,
        stack: `Total${index}`,
        showSymbol: false,
        symbol: 'circle',
        symbolSize: 8,
        emphasis: {
          showSymbol: true,
        },
      })),
    };
  }, [chartData, filter, formatMessage, tooltipConfig, type]);

  const empty = useMemo(() => {
    return !chartData?.newArr?.length;
  }, [chartData]);

  return (
    <EChartCommon
      options={options}
      optionsProps={{ type: chartType }}
      ref={eChartsRef}
      empty={empty}
      rightTitle={chartTitle({
        formatMessage,
        title: rightTitle,
        titleTipsKey: rightTitleTips,
      })}
      {...props}
      emptyHeight={props.height - 18}
    />
  );
}
